import React, { useState } from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import type { DecisionOutcome } from '../../types/developmentDemo';
import { formatTimestamp } from '../../utils/demoStorage';
import { errorClass, fieldClass, labelClass, primaryButton } from '../../utils/demoUi';
import { decisionLabels, participantName } from '../../utils/developmentDemo';
const outcomes = Object.keys(decisionLabels) as DecisionOutcome[];

/** A human hiring-stage decision, kept separate from development findings. */
export function DecisionForm({
  participantId
}: {
  participantId: string;
}) {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const draft = state.ui.decisionDrafts[participantId] ?? {
    outcome: '',
    reason: ''
  };
  const reviewed = state.reviews.filter(review => review.participantId === participantId && review.outcome === 'reviewed');
  const decisions = state.decisions.filter(decision => decision.participantId === participantId);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const name = participantName(config, participantId);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (reviewed.length === 0) return setError('A decision needs at least one completed review.');
    if (!draft.outcome) return setError('Choose a decision.');
    if (!draft.reason.trim()) return setError('Write the reason for this decision.');
    dispatch({
      type: 'recordDecision',
      participantId
    });
    setError('');
    setMessage(`Decision recorded for ${name}.`);
  };
  return <form onSubmit={submit} noValidate aria-label={`Human decision for ${name}`} className="rounded-xl border border-line bg-white p-5" data-id="element-602" data-mp="5ospqcuik2q1">
      <p className="font-heading font-bold text-ink-dark" data-id="element-603" data-mp="xua7fhcn8nu">Human decision · {name}</p>
      <p className="mt-0.5 text-xs text-ink-muted" data-id="element-604" data-mp="39dduvq3yfira">
        Based on reviewed evidence: {reviewed.length > 0 ? reviewed.map(review => review.id).join(', ') : 'none yet'}
      </p>
      <fieldset className="mt-3" data-id="element-605" data-mp="18bo8apgg98zo">
        <legend className="sr-only" data-id="element-606" data-mp="18amuwebyoaes">Decision</legend>
        <div className="flex flex-col gap-2" data-id="element-607" data-mp="189ts1og4i51x">
          {outcomes.map(outcome => <label key={outcome} className="flex items-center gap-2 text-sm text-ink-dark" data-id="element-608" data-mp="1d8oagpa6hrli">
              <input type="radio" name={`decision-${participantId}`} checked={draft.outcome === outcome} disabled={reviewed.length === 0} onChange={() => {
            setMessage('');
            dispatch({
              type: 'setDecisionDraft',
              participantId,
              draft: {
                ...draft,
                outcome
              }
            });
          }} className="h-4 w-4 accent-[#6366C9]" data-id="element-609" data-mp="1i3kgwug5whi5" />
              {decisionLabels[outcome]}
            </label>)}
        </div>
      </fieldset>
      <label htmlFor={`decision-reason-${participantId}`} className={`mt-3 ${labelClass}`} data-id="element-610" data-mp="2ap3j6mise6kz">
        Reason
      </label>
      <textarea id={`decision-reason-${participantId}`} rows={2} value={draft.reason} disabled={reviewed.length === 0} onChange={event => dispatch({
      type: 'setDecisionDraft',
      participantId,
      draft: {
        ...draft,
        reason: event.target.value
      }
    })} className={fieldClass} data-id="element-611" data-mp="2eq5j75kj2mo0" />
      {error && <p className={errorClass} data-id="element-612" data-mp="3qf4gpba79iq7">{error}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-3" data-id="element-613" data-mp="3lhbbolkmuuri">
        <button type="submit" disabled={reviewed.length === 0} className={primaryButton} data-id="element-614" data-mp="3gj9t01r4cxgk">
          Save Decision
        </button>
        <p role="status" className="text-sm font-medium text-navy" data-id="element-615" data-mp="3bjx3f98v5caa">
          {message}
        </p>
      </div>

      {decisions.length > 0 && <ol aria-label={`Decision history for ${name}`} className="mt-4 space-y-2 border-t border-line pt-3" data-id="element-616" data-mp="37kphbkjz7o1u">
          {[...decisions].reverse().map(decision => <li key={decision.id} className="text-sm" data-id="element-617" data-mp="15px9ss5itzl9">
              <span className="font-semibold text-ink-dark" data-id="element-618" data-mp="10sewyqodv07z">{decisionLabels[decision.outcome]}</span>
              <span className="text-ink-muted" data-id="element-619" data-mp="3tmvmbsksjd45"> · {decision.id} · {formatTimestamp(decision.at)}</span>
              <span className="block text-ink-muted" data-id="element-620" data-mp="2guh40nibdqf">Reason: {decision.reason}</span>
            </li>)}
        </ol>}
    </form>;
}